import { useEffect, useMemo, useRef, useState } from 'react';
import type { Editor } from '@tiptap/react';
import { Flex, Icon, Text, Input, icon as iconSize, useKeyboardSelectableList } from '@nula/core-ui';
import { FloatingPanel } from '@/components/floating-panel/floating-panel';
import { kindIcon } from '@/lib/documents/doc-title';
import { useWorkspaceDocs } from '@/lib/sync/hooks/use-workspace-docs';
import { filterPickerDocs } from './doc-picker-filter';
import styles from './editor-doc-picker.module.css';

export interface EditorDocPickerProps {
  editor: Editor;
  /** The document being edited — excluded from results so it can't self-link. */
  currentEntityId: string;
}

/**
 * Document picker for inserting a nula-link — a caret-anchored popover with
 * search (mirrors the Lexical wiki-link picker, NOT a modal). Opened by typing
 * `[[` (RichTextEditor dispatches `nula-show-doc-picker`). Pick a document and a
 * nula-link chip is inserted via the NulaLinkExtension's `insertNulaLink`.
 */
export function EditorDocPicker({ editor, currentEntityId }: EditorDocPickerProps) {
  const [open, setOpen] = useState(false);
  const [query, setQuery] = useState('');
  const [pos, setPos] = useState<{ top: number; left: number }>({ top: 0, left: 0 });
  const docs = useWorkspaceDocs();

  useEffect(() => {
    const show = () => {
      // Anchor below the caret, like the slash menu.
      const { from } = editor.state.selection;
      const coords = editor.view.coordsAtPos(from);
      setPos({ top: coords.bottom + 8, left: coords.left });
      setQuery('');
      setOpen(true);
    };
    window.addEventListener('nula-show-doc-picker', show);
    return () => window.removeEventListener('nula-show-doc-picker', show);
  }, [editor]);

  // Close on outside click / Escape.
  const panelRef = useRef<HTMLDivElement>(null);
  useEffect(() => {
    if (!open) return;
    const onPointerDown = (e: MouseEvent) => {
      if (panelRef.current && !panelRef.current.contains(e.target as Node)) setOpen(false);
    };
    const onKey = (e: KeyboardEvent) => {
      if (e.key === 'Escape') setOpen(false);
    };
    document.addEventListener('mousedown', onPointerDown);
    document.addEventListener('keydown', onKey);
    return () => {
      document.removeEventListener('mousedown', onPointerDown);
      document.removeEventListener('keydown', onKey);
    };
  }, [open]);

  const results = useMemo(
    () => filterPickerDocs(docs, query, currentEntityId),
    [docs, query, currentEntityId],
  );

  const { highlightedIndex, getItemProps, getListProps } = useKeyboardSelectableList({
    items: results,
    onSelect: (doc) => {
      editor.chain().focus().insertNulaLink({ documentId: doc.id, title: doc.title || 'Untitled' }).run();
      setOpen(false);
    },
  });

  if (!open) return null;

  return (
    <FloatingPanel ref={panelRef} top={pos.top} left={pos.left} minWidth={280}>
      <Flex vertical gap="xs" className={styles.body}>
        <Input
          autoFocus
          placeholder="Link to document…"
          value={query}
          onChange={(e) => setQuery(e.target.value)}
          {...getListProps()}
        />
        <Flex vertical className={styles.list}>
          {results.length === 0 ? (
            <Text type="secondary" className={styles.empty}>
              No matching documents
            </Text>
          ) : (
            results.map((doc, i) => (
              <button
                key={doc.id}
                type="button"
                className={styles.item}
                data-active={i === highlightedIndex}
                {...getItemProps(i)}
              >
                <Icon name={kindIcon(doc.kind)} size={iconSize.sm} className={styles.itemIcon} />
                <Text className={styles.itemLabel}>{doc.title || 'Untitled'}</Text>
              </button>
            ))
          )}
        </Flex>
      </Flex>
    </FloatingPanel>
  );
}
